import { useEffect, useState } from 'react';

import {
  useGetMedicalDocumentsFilterDataQuery,
  useLazySearchMedicalDocumentsQuery,
} from '@app/api/MedicalDocuments';
import type { ModelMedicalDocumentResponse } from '@app/api/MedicalDocuments';
import { CDA_4_KIND } from '@app/components/RecordModal/components/MedicalCard/data';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';

export const useMedicalCardRegistryEffects = () => {
  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const { showNotification } = useNotification();
  const [tableData, setTableData] = useState<ModelMedicalDocumentResponse[]>(
    []
  );
  const [totalRows, setTotalRows] = useState(0);

  const {
    data: filtersData,
    isLoading: isFiltersLoading,
    error: filtersError,
  } = useGetMedicalDocumentsFilterDataQuery(
    { ActiveBranchId },
    {
      skip: !ActiveBranchId,
    }
  );

  const [
    searchMedicalDocuments,
    { isLoading: isSearchLoading, error: searchError },
  ] = useLazySearchMedicalDocumentsQuery();

  useEffect(() => {
    const getInitialTableData = async () => {
      try {
        if (!filtersData) return;

        const { Items, Total } = await searchMedicalDocuments({
          ActiveBranchId,
          PatientId: null,
          DoctorId: null,
          Statuses: null,
          Kind: CDA_4_KIND,
          IsSigned: false,
          SignedByUserId: null,
          DateFrom: null,
          DateTo: null,
          DoctorSignedDateFrom: null,
          DoctorSignedDateTo: null,
          ChiefSignedDateFrom: null,
          ChiefSignedDateTo: null,
          SearchTerm: null,
          Page: 1,
          PageSize: 20,
        }).unwrap();

        setTableData(Items ?? []);
        setTotalRows(Total);
      } catch (error) {
        showNotification(handleCatchError(error));
      }
    };

    getInitialTableData();
  }, [ActiveBranchId, filtersData, searchMedicalDocuments, showNotification]);

  useEffect(() => {
    if (filtersError) {
      showNotification(handleCatchError(filtersError));
    }
  }, [filtersError, showNotification]);

  useEffect(() => {
    if (searchError) {
      showNotification(handleCatchError(searchError));
    }
  }, [searchError, showNotification]);

  return {
    isFiltersLoading,
    filtersData,
    isSearchLoading,
    tableData,
    totalRows,
    setTableData,
    setTotalRows,
  };
};
